import { createContext, forwardRef, useContext, useMemo } from 'react';

import { Button, IconButton } from 'components/Button';
import { CaretDownIcon, CaretUpIcon } from 'components/Icon';
import { cn } from 'components/utils';

import { splitButtonRecipe, splitButtonTriggerRecipe } from './SplitButton.recipe';

import type { SplitButtonActionProps, SplitButtonProps, SplitButtonTriggerProps } from './types';
import type { ButtonSize } from 'components/Button/types';

type SplitButtonContextValue = Required<Pick<SplitButtonProps, 'appearance' | 'disabled' | 'loading' | 'fullWidth'>> & {
  size: ButtonSize;
};

const SplitButtonContext = createContext<SplitButtonContextValue>({
  size: 'regular',
  appearance: 'primary',
  disabled: false,
  loading: false,
  fullWidth: false,
});

/**
 * Design System 2.0 split button — a main action joined to a chevron that opens a menu of related
 * choices.
 *
 * Figma draws it as two sets (file 5aSkE0U4TeWbuIZDvccHjc): `Split button` (1993:130584), a labelled
 * action with the chevron after it, and `Split toggle` (1993:131183), the call bar's chevron-first
 * device control. They are one component here because they differ only in which half comes first and
 * what the other half is:
 *
 * ```tsx
 * // Split button
 * <SplitButton>
 *   <SplitButtonAction onClick={joinAsHost}>{t('joinAsHost')}</SplitButtonAction>
 *   <SplitButtonTrigger aria-label={t('joinOptions')} onClick={openMenu} />
 * </SplitButton>
 *
 * // Split toggle: any toggle button in the second half
 * <SplitButton size="large" appearance="secondary">
 *   <SplitButtonTrigger direction="up" aria-label={t('audioSettings')} onClick={openSettings} />
 *   <IconButton size="large" appearance="destructive" fill="muted" isSelected aria-label={t('unmute')}>…</IconButton>
 * </SplitButton>
 * ```
 *
 * Each half is its own `<button>` and its own tab stop, so the group is `role="group"` rather than
 * one control. `size`, `appearance`, `disabled` and `loading` on the root reach both parts through
 * context; a part's own prop wins. A half the caller supplies itself (the toggle) reads nothing from
 * context and takes its size directly.
 *
 * Anchor a `Menu` (or a legacy menu) to the trigger, and pass `aria-haspopup="menu"` and
 * `aria-expanded` so the half is announced as a menu button and draws its pressed cell while open.
 * Use a plain `Button` that opens a `Menu` instead when no single choice is the obvious one.
 */
export const SplitButton = forwardRef<HTMLDivElement, SplitButtonProps>(
  (
    {
      size = 'regular',
      appearance = 'primary',
      disabled = false,
      loading = false,
      fullWidth = false,
      className,
      children,
      ...rest
    },
    ref
  ) => {
    const value = useMemo(
      () => ({ size, appearance, disabled, loading, fullWidth }),
      [size, appearance, disabled, loading, fullWidth]
    );

    return (
      <SplitButtonContext.Provider value={value}>
        <div ref={ref} role="group" className={cn(splitButtonRecipe({ size, fullWidth }), className)} {...rest}>
          {children}
        </div>
      </SplitButtonContext.Provider>
    );
  }
);

/** The labelled half. A `Button` whose size and appearance come from the group. */
export const SplitButtonAction = forwardRef<HTMLButtonElement, SplitButtonActionProps>(
  ({ loading, disabled, className, children, startIcon, ...rest }, ref) => {
    const group = useContext(SplitButtonContext);

    return (
      <Button
        ref={ref}
        size={group.size}
        appearance={group.appearance}
        loading={loading ?? group.loading}
        disabled={disabled ?? group.disabled}
        startIcon={startIcon}
        // `grow` rather than `fullWidth`: the button's own `w-full` would push the trigger out of a
        // flex row, while growing lets the label take the slack and leaves the chevron square.
        className={cn(group.fullWidth && 'grow', className)}
        {...rest}
      >
        {children}
      </Button>
    );
  }
);

/**
 * The chevron half. An icon-only `Button` in the group's size and appearance.
 *
 * Disabled while the group is loading, as Figma's `State=Loading` draws it, so the choice cannot
 * change mid-request. It stays live when only the ACTION is disabled: in the call bar a blocked
 * microphone keeps its settings menu, which is how someone fixes the permission.
 */
export const SplitButtonTrigger = forwardRef<HTMLButtonElement, SplitButtonTriggerProps>(
  ({ direction = 'down', disabled, className, ...rest }, ref) => {
    const group = useContext(SplitButtonContext);

    return (
      <IconButton
        ref={ref}
        size={group.size}
        appearance={group.appearance}
        disabled={disabled ?? (group.disabled || group.loading)}
        className={cn(splitButtonTriggerRecipe(), className)}
        {...rest}
      >
        {direction === 'up' ? <CaretUpIcon /> : <CaretDownIcon />}
      </IconButton>
    );
  }
);
